import React, { forwardRef, startTransition, useCallback, useEffect, useImperativeHandle, useRef } from 'react';
import { flushSync } from 'react-dom';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import cancelIcon from '../../assets/Icon/project/cancel.png';
import { isImageSizeAllowed } from '../../Utils/imageUpload';

export type RichTextEditorHandle = {
  openImagePicker: () => void;
  /** 포커스만 옮깁니다. 커서(선택) 위치는 유지하거나 브라우저 기본값을 따릅니다. */
  focus: () => void;
  /** 본문 끝으로 커서를 이동한 뒤 포커스합니다. (스텝 진입·최초 탭 등) */
  focusAtEnd: () => void;
};

type Props = {
  value: string;
  onChange: (html: string) => void;
  placeholder: string;
  pictureIconSrc: string;
  maxImages: number;
  imageCount: number;
  pinnedImageSrc?: string;
  pinnedVideoEmbedUrl?: string;
  pinnedBadgeLabel?: string;
  showToolbar?: boolean;
  previewHeightPx?: number;
  onFocus?: () => void;
  onBlur?: () => void;
  onEnterKey?: () => void;
  onImageInserted?: () => void;
  editorBottomPaddingPx?: number;
  /**
   * If true, 대표 이미지를 에디터(입력) 최상단에 고정 삽입합니다.
   * (요청: step3 input 제일 상단에 대표 이미지)
   */
  pinnedInsideEditor?: boolean;
  /**
   * Optional image upload handler.
   * If provided, this function is called when a file is selected.
   * It should upload the file and return the public URL.
   */
  onImageUpload?: (file: File) => Promise<string>;
  /** 모달 등 스크롤 영역 안에서 사진 추가 버튼을 하단에 고정(sticky) */
  stickyToolbar?: boolean;
  /** Wrap 상단 마진(px). 모달·노트북에서 에디터 위 공간 축소 시 4~6 등 */
  wrapMarginTopPx?: number;
  /**
   * true면 흰 카드 패딩·최소 높이 없이 본문만 표시(프로젝트 설정 모바일 사역 소개 등)
   */
  flushChrome?: boolean;
};

/**
 * Lightweight Rich Text Editor (contentEditable)
 * - "사진 추가" 시 커서 위치에 이미지 삽입
 * - pinnedImageSrc가 있으면 대표 미디어를 입력 영역에 고정 표기
 */
const RichTextEditor = forwardRef<RichTextEditorHandle, Props>(function RichTextEditor(
  {
    value,
    onChange,
    placeholder,
    pictureIconSrc,
    maxImages,
    imageCount,
    pinnedImageSrc,
    pinnedVideoEmbedUrl,
    pinnedBadgeLabel,
    showToolbar = true,
    previewHeightPx = 240,
    onFocus,
    onBlur,
    onEnterKey,
    onImageInserted,
    editorBottomPaddingPx = 0,
    pinnedInsideEditor = true,
    onImageUpload,
    stickyToolbar = false,
    wrapMarginTopPx = 12,
    flushChrome = false,
  }: Props,
  ref,
) {
  const { t } = useTranslation('translation');
  const editorRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const objectUrlsRef = useRef<string[]>([]);
  const resolvedPinnedBadgeLabel = pinnedBadgeLabel ?? t('common.labels.main');
  const hasPinnedMedia = Boolean(pinnedImageSrc || pinnedVideoEmbedUrl);

  const canAddImages = imageCount < maxImages;

  /** 이미지 삽입/삭제는 HTML이 커지므로 React 업데이트를 낮은 우선순위로 넘겨 메인 스레드 버벅임 완화 */
  const pushContentToParent = useCallback(
    (el: HTMLDivElement, scheduleImageInserted?: boolean) => {
      const next = serializeWithoutPinned(el);
      setEmptyAttr(el, next);
      // 이미지 삽입 직후 startTransition만 쓰면 effect(value)가 한 박자 늦게 와서 stale value로 innerHTML을 덮을 수 있음
      if (scheduleImageInserted) {
        flushSync(() => {
          onChange(next);
        });
      } else {
        startTransition(() => {
          onChange(next);
        });
      }
      if (scheduleImageInserted) {
        requestAnimationFrame(() => onImageInserted?.());
      }
    },
    [onChange, onImageInserted],
  );

  useImperativeHandle(
    ref,
    () => ({
      openImagePicker: () => {
        if (!canAddImages) return;
        const input = inputRef.current;
        if (!input) return;
        const picker = (input as HTMLInputElement & { showPicker?: () => void }).showPicker;
        if (typeof picker === 'function') {
          picker.call(input);
          return;
        }
        input.click();
      },
      focus: () => {
        editorRef.current?.focus();
      },
      focusAtEnd: () => {
        placeCaretAtEnd(editorRef.current);
      },
    }),
    [canAddImages],
  );

  useEffect(() => {
    return () => {
      objectUrlsRef.current.forEach((u) => URL.revokeObjectURL(u));
      objectUrlsRef.current = [];
    };
  }, []);

  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    const cur = serializeWithoutPinned(el);
    if (cur !== value) {
      el.innerHTML = value;
      // Inject delete buttons to existing figures
      el.querySelectorAll('figure[data-embed="story-image"]').forEach((fig) => {
        if (!fig.querySelector('button[data-embed="delete-btn"]')) {
          const delBtn = document.createElement('button');
          delBtn.setAttribute('data-embed', 'delete-btn');
          delBtn.type = 'button';
          const iconImg = document.createElement('img');
          iconImg.src = cancelIcon;
          iconImg.alt = t('common.actions.delete');
          iconImg.setAttribute('width', '32');
          iconImg.setAttribute('height', '32');
          delBtn.appendChild(iconImg);
          fig.appendChild(delBtn);
        }
        (fig as HTMLElement).contentEditable = 'false';
      });
      requestAnimationFrame(() => {
        placeCaretAtEnd(el);
      });
    }
    setEmptyAttr(el, value);
  }, [value, t]);

  const insertImageAtCaret = (src: string, preferInsertAfter?: Node | null) => {
    const el = editorRef.current;
    if (!el) return;

    const fig = document.createElement('figure');
    fig.setAttribute('data-embed', 'story-image');
    fig.contentEditable = 'false';

    const img = document.createElement('img');
    img.src = src;
    img.alt = '';
    img.setAttribute('data-embed', 'image');
    img.setAttribute('width', '100%');
    fig.appendChild(img);

    const delBtn = document.createElement('button');
    delBtn.setAttribute('data-embed', 'delete-btn');
    delBtn.type = 'button';
    const iconImg = document.createElement('img');
    iconImg.src = cancelIcon;
    iconImg.alt = t('common.actions.delete');
    iconImg.setAttribute('width', '32');
    iconImg.setAttribute('height', '32');
    delBtn.appendChild(iconImg);
    fig.appendChild(delBtn);

    const tailBr = document.createElement('br');

    const collapseAfter = (node: Node) => {
      const s = window.getSelection?.();
      if (!s) return;
      const r = document.createRange();
      r.setStartAfter(node);
      r.collapse(true);
      s.removeAllRanges();
      s.addRange(r);
    };

    /** React/value 동기화 직후에도 방금 넣은 figure 바로 아래에서 입력할 수 있도록 */
    const scheduleCaretBelowFigure = (insertedFig: HTMLElement) => {
      const restore = () => {
        const ed = editorRef.current;
        if (!ed || !ed.contains(insertedFig)) return;
        let after = insertedFig.nextSibling;
        if (!after || after.nodeName !== 'BR') {
          const b = document.createElement('br');
          insertedFig.insertAdjacentElement('afterend', b);
          after = b;
        }
        collapseAfter(after);
        ed.focus();
      };
      requestAnimationFrame(() => {
        requestAnimationFrame(restore);
      });
    };

    /** 같은 파일 선택 세션에서 `await` 뒤 selection 이 사라져도, 항상 이전 장 바로 아래에 이어 붙음 */
    if (preferInsertAfter instanceof Element && el.contains(preferInsertAfter)) {
      preferInsertAfter.insertAdjacentElement('afterend', fig);
      fig.insertAdjacentElement('afterend', tailBr);
      el.focus();
      collapseAfter(tailBr);
      pushContentToParent(el, true);
      scheduleCaretBelowFigure(fig);
      return;
    }

    el.focus();
    const sel = window.getSelection?.();

    if (!sel || sel.rangeCount === 0 || !el.contains(sel.getRangeAt(0).commonAncestorContainer)) {
      el.appendChild(fig);
      el.appendChild(tailBr);
      collapseAfter(tailBr);
      pushContentToParent(el, true);
      scheduleCaretBelowFigure(fig);
      return;
    }

    /** 파일 피커·비동기 업로드 후 selection 이 (0,0)으로 돌아가 본문 맨 위에 붙는 브라우저 동작 보정 */
    if (
      hasMeaningfulEditorContent(el) &&
      isCollapsedCaretAtEditorVeryStart(el, sel.getRangeAt(0))
    ) {
      const anchor = getTailBrAfterLastStoryImage(el);
      if (anchor instanceof Element && el.contains(anchor)) {
        anchor.insertAdjacentElement('afterend', fig);
        fig.insertAdjacentElement('afterend', tailBr);
        collapseAfter(tailBr);
        pushContentToParent(el, true);
        scheduleCaretBelowFigure(fig);
        return;
      }
      el.appendChild(fig);
      el.appendChild(tailBr);
      collapseAfter(tailBr);
      pushContentToParent(el, true);
      scheduleCaretBelowFigure(fig);
      return;
    }

    const range = sel.getRangeAt(0);
    range.deleteContents();
    range.insertNode(fig);
    // Collapsed range is immediately after `fig`; insert trailing line break for a visible caret below the image.
    range.insertNode(tailBr);
    sel.removeAllRanges();
    sel.addRange(range);

    pushContentToParent(el, true);
    scheduleCaretBelowFigure(fig);
  };

  const onFiles = async (files: FileList | null) => {
    if (!files) return;
    const getCurrentEmbeddedImageCount = () => {
      const el = editorRef.current;
      if (!el) return imageCount;
      return el.querySelectorAll('figure[data-embed="story-image"]').length;
    };

    // Use live DOM count as a source of truth to avoid stale-prop race conditions.
    const currentCount = Math.max(imageCount, getCurrentEmbeddedImageCount());
    const remaining = Math.max(0, maxImages - currentCount);
    if (remaining <= 0) return;
    const list = Array.from(files).slice(0, remaining);

    /** 여러 장 연속 삽입 시 각 `await` 사이에 selection 이 없어지므로, DOM 앵커로 순서 고정 */
    let insertAfterNode: Node | null = null;

    for (const f of list) {
      if (getCurrentEmbeddedImageCount() >= maxImages) break;
      if (!isImageSizeAllowed(f)) {
        alert(t('common.errors.imageTooLarge'));
        continue;
      }
      try {
        let src = '';
        if (onImageUpload) {
          try {
            src = await onImageUpload(f);
          } catch (uploadErr) {
            console.error('Image upload failed:', uploadErr);
            alert(t('page.newProject.errors.imageReadFailed'));
            continue;
          }
        } else {
          // 모바일: data URL 대신 blob URL 사용 (clone/상태 문자열 부담 대폭 감소). 원본을 그대로 사용한다.
          const url = URL.createObjectURL(f);
          objectUrlsRef.current.push(url);
          src = url;
        }

        if (src) {
          const elNow = editorRef.current;
          if (!elNow) break;
          if (insertAfterNode && !elNow.contains(insertAfterNode)) {
            insertAfterNode = getTailBrAfterLastStoryImage(elNow);
          }
          insertImageAtCaret(src, insertAfterNode);
          insertAfterNode = getTailBrAfterLastStoryImage(elNow);
        }
      } catch (err) {
        console.error('Failed to process file', err);
      }
    }
    
    // allow same file re-pick
    if (inputRef.current) inputRef.current.value = '';
  };

  return (
    <Wrap $marginTop={flushChrome ? 0 : wrapMarginTopPx}>
      {!pinnedInsideEditor && hasPinnedMedia && (
        <PinnedMedia style={{ height: `${previewHeightPx}px` }}>
          {pinnedImageSrc ? (
            <img src={pinnedImageSrc} alt="" data-embed="image" />
          ) : pinnedVideoEmbedUrl ? (
            <iframe
              src={pinnedVideoEmbedUrl}
              title="Pinned video preview"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              data-embed="video"
            />
          ) : null}
          <div data-embed="badge">✓ {resolvedPinnedBadgeLabel}</div>
        </PinnedMedia>
      )}
      <EditorContainer $flush={flushChrome}>
        {pinnedInsideEditor && hasPinnedMedia && (
          <PinnedMedia style={{ height: `${previewHeightPx}px` }}>
            {pinnedImageSrc ? (
              <img src={pinnedImageSrc} alt="" data-embed="image" />
            ) : pinnedVideoEmbedUrl ? (
              <iframe
                src={pinnedVideoEmbedUrl}
                title="Pinned video preview"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                data-embed="video"
              />
            ) : null}
            <div data-embed="badge">✓ {resolvedPinnedBadgeLabel}</div>
          </PinnedMedia>
        )}
        <Editor
          ref={editorRef}
          $bottomPaddingPx={editorBottomPaddingPx}
          $flush={flushChrome}
          contentEditable
          suppressContentEditableWarning
          data-placeholder={placeholder}
          onFocus={() => {
            onFocus?.();
          }}
          onBlur={() => {
            onBlur?.();
          }}
          onInput={(e) => {
            const el = e.currentTarget as HTMLDivElement;
            const next = serializeWithoutPinned(el);
            setEmptyAttr(el, next);
            onChange(next);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
              onEnterKey?.();
            }
          }}
          onClick={(e) => {
            const target = e.target as HTMLElement;
            const deleteBtn = target.closest('button[data-embed="delete-btn"]');
            if (deleteBtn) {
              const fig = deleteBtn.closest('figure');
              if (fig) {
                const img = fig.querySelector('img[data-embed="image"]') as HTMLImageElement | null;
                const imgSrc = img?.getAttribute('src') ?? '';
                if (imgSrc.startsWith('blob:')) {
                  try {
                    URL.revokeObjectURL(imgSrc);
                  } catch {
                    /* ignore */
                  }
                  objectUrlsRef.current = objectUrlsRef.current.filter((u) => u !== imgSrc);
                }
                fig.remove();
                const el = editorRef.current;
                if (el) {
                  pushContentToParent(el, false);
                }
              }
            }
          }}
        />
      </EditorContainer>

      {showToolbar ? (
        <Toolbar $sticky={stickyToolbar}>
          <AddPhotoBtn
            type="button"
            onClick={() => {
              const input = inputRef.current;
              if (!input) return;
              const picker = (input as HTMLInputElement & { showPicker?: () => void }).showPicker;
              if (typeof picker === 'function') {
                picker.call(input);
                return;
              }
              input.click();
            }}
            disabled={!canAddImages}
          >
            <AddPhotoIcon src={pictureIconSrc} alt="" />
            사진 추가({imageCount}/{maxImages})
          </AddPhotoBtn>
        </Toolbar>
      ) : null}

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        style={{
          position: 'absolute',
          width: 1,
          height: 1,
          padding: 0,
          margin: -1,
          overflow: 'hidden',
          clip: 'rect(0, 0, 0, 0)',
          whiteSpace: 'nowrap',
          border: 0,
        }}
        onChange={(e) => onFiles(e.target.files)}
      />
    </Wrap>
  );
});

export default RichTextEditor;

function hasMeaningfulEditorContent(el: HTMLDivElement): boolean {
  if (el.querySelector('figure[data-embed="story-image"]')) return true;
  const t = (el.textContent ?? '').replace(/\u00a0/g, ' ').trim();
  return t.length > 0;
}

/**
 * contentEditable 루트 맨 앞(자식 노드 0번 앞)에 접힌 커서만 true.
 * (파일 피커 복귀 후 브라우저가 흔히 두는 위치 — 실제 타이핑 커서와 구분)
 */
function isCollapsedCaretAtEditorVeryStart(el: HTMLDivElement, range: Range): boolean {
  if (!range.collapsed) return false;
  return range.startContainer === el && range.startOffset === 0;
}

/** 마지막 본문(story) 이미지 블록 바로 뒤의 줄바꿈 노드 — 다중 선택 업로드 시 연쇄 삽입 앵커로 사용 */
function getTailBrAfterLastStoryImage(el: HTMLDivElement): Node | null {
  const figs = el.querySelectorAll('figure[data-embed="story-image"]');
  if (!figs.length) return null;
  const lastFig = figs[figs.length - 1] as HTMLElement;
  let n = lastFig.nextSibling;
  if (n?.nodeName === 'BR') return n;
  const b = document.createElement('br');
  lastFig.insertAdjacentElement('afterend', b);
  return b;
}

function serializeWithoutPinned(el: HTMLDivElement) {
  // 삭제 버튼이 없으면 전체 트리 clone 생략 (typing·짧은 HTML에서 비용 큼)
  if (!el.querySelector('button[data-embed="delete-btn"]')) {
    return el.innerHTML;
  }
  const clone = el.cloneNode(true) as HTMLDivElement;
  clone.querySelectorAll(`button[data-embed="delete-btn"]`).forEach((n) => n.remove());
  return clone.innerHTML;
}

function htmlIsTextEmpty(html: string): boolean {
  if (!html || html.trim() === '') return true;
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  if (tmp.querySelector('figure[data-embed="story-image"]')) return false;
  const text = (tmp.textContent ?? '')
    .replace(/\u00a0/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return text === '';
}

/**
 * 플레이스홀더 표시: 실제 글/스토리 이미지가 없고, 줄바꿈(엔터)만 만든 빈 블록 구조도 "비어 있지 않음"으로 봄.
 * (컨텐트가 없을 때 브라우저가 두는 단일 루트 br만 허용)
 */
function shouldShowEditorPlaceholder(html: string): boolean {
  if (!htmlIsTextEmpty(html)) return false;
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  tmp.querySelectorAll('figure[data-embed="story-image"]').forEach((n) => n.remove());

  const significantChildNodes = Array.from(tmp.childNodes).filter((n) => {
    if (n.nodeType === Node.TEXT_NODE) return (n.textContent ?? '').trim().length > 0;
    return true;
  });
  if (significantChildNodes.length === 0) return true;
  if (significantChildNodes.length === 1 && significantChildNodes[0].nodeName === 'BR') return true;
  return false;
}

/** 대표(핀) 미디어는 value 밖에 있음 — 본문 HTML 기준으로 플레이스홀더 여부만 판단. */
function setEmptyAttr(el: HTMLDivElement, html: string) {
  el.dataset.textEmpty = shouldShowEditorPlaceholder(html) ? 'true' : 'false';
}

/** Collapsed selection after the last child (natural typing below figures / pinned sibling content). */
function placeCaretAtEnd(el: HTMLDivElement | null) {
  if (!el) return;
  const lastFigure = el.querySelector('figure[data-embed="story-image"]:last-of-type');
  const currentLastNode = el.lastChild;
  const currentLastElement = el.lastElementChild;

  // 마지막이 이미지(figure)이거나 마지막 노드가 <br>가 아니면
  // "사진 바로 아래" 입력용 앵커 <br>를 강제로 만들어 커서를 안정적으로 고정한다.
  if (
    (currentLastElement && currentLastElement.tagName === 'FIGURE')
    || (lastFigure && currentLastNode?.nodeName !== 'BR')
  ) {
    el.appendChild(document.createElement('br'));
  } else if (!currentLastNode) {
    el.appendChild(document.createElement('br'));
  }

  el.focus();
  const sel = window.getSelection?.();
  if (!sel) return;
  const range = document.createRange();
  const anchorNode = el.lastChild;
  if (!anchorNode) {
    range.setStart(el, 0);
    range.collapse(true);
  } else if (anchorNode.parentNode === el) {
    range.setStartAfter(anchorNode);
    range.collapse(true);
  } else {
    const n = el.childNodes.length;
    range.setStart(el, n);
    range.collapse(true);
  }
  sel.removeAllRanges();
  sel.addRange(range);
}

const Wrap = styled.div<{ $marginTop: number }>`
  margin-top: ${({ $marginTop }) => $marginTop}px;
`;

const EditorContainer = styled.div<{ $flush?: boolean }>`
  margin-top: ${({ $flush }) => ($flush ? '0' : '-4px')};
  width: 100%;
  min-height: ${({ $flush }) => ($flush ? '0' : '260px')};
  border-radius: ${({ $flush }) => ($flush ? '0' : '10px')};
  padding: ${({ $flush }) => ($flush ? '0' : '12px')};
  background: ${({ $flush }) => ($flush ? 'transparent' : '#ffffff')};
  display: flex;
  flex-direction: column;
`;

const PinnedMedia = styled.figure`
  position: relative;
  margin: 0 0 12px;
  border-radius: 10px;
  overflow: hidden;
  border: 2.4px solid #2bc2e1;
  background: #f0f1f2;
  flex-shrink: 0;

  img[data-embed='image'] {
    width: 100%;
    height: 100%;
    display: block;
    object-fit: cover;
  }

  iframe[data-embed='video'] {
    width: 100%;
    height: 100%;
    display: block;
    border: none;
  }

  div[data-embed='badge'] {
    position: absolute;
    top: 12px;
    left: 12px;
    display: flex;
    max-width: calc(100% - 24px);
    min-width: 64px;
    min-height: 28px;
    justify-content: center;
    align-items: center;
    gap: 4px;
    flex-shrink: 0;
    border-radius: 4px;
    padding: 2px 10px;
    background: ${({ theme }) => theme.colors.primaryBlue[40]};
    ${({ theme }) => theme.typography.caption.regular16}
    color: ${({ theme }) => theme.colors.grayScale[0]};
    box-sizing: border-box;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const Editor = styled.div<{ $bottomPaddingPx?: number; $flush?: boolean }>`
  flex: ${({ $flush }) => ($flush ? '0 1 auto' : '1')};
  width: 100%;
  position: relative;
  padding-bottom: ${({ $bottomPaddingPx }) => ($bottomPaddingPx ? `${$bottomPaddingPx}px` : '0px')};
  font-family: Pretendard;
  font-size: 20px;
  font-weight: 400;
  line-height: 150%;
  color: #17171b;
  outline: none;
  overflow: visible;

  /*
   * 플레이스홀더는 흐름 밖(absolute)에 두어, 빈 에디터에서 커서가 첫 줄·플레이스홀더와 같은 위치에 오도록 함.
   * (::before를 블록 흐름에 두면 가짜 한 줄이 생겨 커서가 그 아래로 밀림)
   */
  &[data-text-empty='true'] {
    min-height: 1.5em;
  }

  &[data-text-empty='true']::before {
    content: attr(data-placeholder);
    position: absolute;
    left: 0;
    top: 0;
    right: 0;
    color: #929292;
    font-size: 20px;
    font-weight: 400;
    line-height: 150%;
    pointer-events: none;
    user-select: none;
    white-space: pre-wrap;
    word-break: break-word;
    z-index: 0;
  }

  figure[data-embed='story-image'] {
    position: relative;
    margin: 12px 0;
    border-radius: 10px;
    overflow: hidden;
    border: 2.4px solid #2bc2e1;
    background: #f0f1f2;
    width: 100%;
    box-sizing: border-box;
  }

  figure[data-embed='story-image'] > img[data-embed='image'] {
    width: 100%;
    max-width: 100%;
    height: auto;
    display: block;
    object-fit: contain;
  }

  button[data-embed='delete-btn'] {
    position: absolute;
    top: 12px;
    right: 12px;
    width: 32px;
    height: 32px;
    padding: 0;
    border: none;
    border-radius: 4px;
    background: transparent;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10;

    img {
      display: block;
      object-fit: contain;
      width: 32px;
      height: 32px;
    }
  }
`;

const Toolbar = styled.div<{ $sticky?: boolean }>`
  margin-top: 16px;
  display: flex;
  justify-content: center;
  flex-shrink: 0;

  ${({ $sticky }) =>
    $sticky
      ? `
    position: sticky;
    bottom: 0;
    z-index: 5;
    padding-top: 12px;
    padding-bottom: 10px;
    margin-bottom: -4px;
    background: #ffffff;
    box-shadow: 0 -8px 24px rgba(0, 0, 0, 0.08);
  `
      : ''}
`;

const AddPhotoBtn = styled.button`
  height: 56px;
  width: 100%;
  border-radius: 12px;
  border: 2px solid #2bc2e1;
  background: #eafbff;
  color: #2bc2e1;
  font-family: Pretendard;
  font-size: 20px;
  font-weight: 500;
  line-height: 150%;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
`;

const AddPhotoIcon = styled.img`
  width: 22px;
  height: 22px;
  object-fit: contain;
`;


